import { useCallback, useEffect, useRef, useState } from "react";

const VERSION_CHECK_INTERVAL_MS = 1000 * 60 * 1;

interface BuildVersion {
  version: string;
  hash: string;
}

const fetchVersion = async (): Promise<BuildVersion | null> => {
  try {
    // Plain fetch, not the axios API service: version.json is a static file on this domain, so it needs
    // no API base URL or auth headers, and a failed check must not open ReloadErrorModal.
    // PUBLIC_URL keeps the path absolute; a relative URL would resolve against the current route.
    const res = await fetch(`${process.env.PUBLIC_URL}/version.json?ts=${new Date().getTime()}`, { cache: "no-store" });
    if (!res.ok) return null;
    // Throws if the server's SPA fallback returned index.html instead of JSON
    return await res.json();
  } catch (error) {
    console.log("Error checking frontend version", error);
    return null;
  }
};

export const useVersionCheck = (intervalMs: number = VERSION_CHECK_INTERVAL_MS) => {
  const currentHash = useRef("");
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      const version = await fetchVersion();
      const newHash = version?.hash;
      if (cancelled || !newHash) return;

      // First successful response is the build this tab is running
      if (!currentHash.current) {
        currentHash.current = newHash;
        return;
      }

      if (currentHash.current !== newHash) {
        setNewVersionAvailable(true);
      }
    };

    check();
    const timer = setInterval(check, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [intervalMs]);

  const reload = useCallback(() => window.location.reload(), []);
  // "Later" hides the modal; the next check reopens it while the hash still differs
  const later = useCallback(() => setNewVersionAvailable(false), []);

  return { newVersionAvailable, reload, later };
};
